"use client"

import { usePostHog } from "posthog-js/react"
import { useCallback } from "react"

export interface TrackEngagementParams {
  engagement_type: "call" | "sms" | "email"
  direction: "outbound" | "inbound"
  call_source?: "sequence" | "powerdialer" | "c2c" | "manual"
}

export function useEngagementTracking() {
  const posthog = usePostHog()

  const trackEngagement = useCallback(
    (params: TrackEngagementParams) => {
      try {
        // Only include call_source for call engagements
        const eventParams: Record<string, string> = {
          engagement_type: params.engagement_type,
          direction: params.direction,
        }

        if (params.engagement_type === "call" && params.call_source) {
          eventParams.call_source = params.call_source
        }

        posthog.capture("user_created_engagement", eventParams)
      } catch (error) {
        // Silently fail to avoid disrupting core functionality
        console.warn("Failed to track engagement:", error)
      }
    },
    [posthog]
  )

  const trackFeatureUsage = useCallback(
    (featureName: "powerdialer" | "fileupload" | "addtopowerdialer" | "setringlocal" | "vmdropped" | "dispositionedcall" | "nextaction" | "adddnote") => {
      try {
        posthog.capture("user_used_feature", {
          feature_name: featureName,
        })
      } catch (error) {
        // Silently fail to avoid disrupting core functionality
        console.warn("Failed to track feature usage:", error)
      }
    },
    [posthog]
  )

  const trackActivation = useCallback(
    (activationAction: "inviteduser" | "connectedcrm" | "connectedemail") => {
      try {
        posthog.capture("user_activated", {
          activation_action: activationAction,
        })
      } catch (error) {
        // Silently fail to avoid disrupting core functionality
        console.warn("Failed to track activation:", error)
      }
    },
    [posthog]
  )

  const trackLifecycle = useCallback(
    (lifecycleEvent: "trialexpired" | "subscription_peek" | "subscribed" | "canceled") => {
      try {
        posthog.capture("user_changed_account_lifecycle", {
          lifecycle_event: lifecycleEvent,
        })
      } catch (error) {
        // Silently fail to avoid disrupting core functionality
        console.warn("Failed to track lifecycle:", error)
      }
    },
    [posthog]
  )

  return { trackEngagement, trackFeatureUsage, trackActivation, trackLifecycle }
}
